/**
 * Push Notification Hook for Bulaccino Cafe
 * True Web Push with VAPID keys for instant notifications
 * even when browser is closed
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../utils/apiClient';

// Get VAPID key from env or fetch from server
const ENV_VAPID_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications(userId) {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(true);
  const [vapidKey, setVapidKey] = useState(ENV_VAPID_KEY);
  const pollIntervalRef = useRef(null);

  // Check if push notifications are supported
  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
    }
    
    // Fetch VAPID key from server if not in env
    if (!ENV_VAPID_KEY) {
      api.get('/push/vapid-public-key')
        .then(res => {
          if (res.data.publicKey) {
            setVapidKey(res.data.publicKey);
          }
        })
        .catch(err => console.warn('Could not fetch VAPID key:', err));
    }
    
    setLoading(false);
  }, []);

  // Check existing subscription
  useEffect(() => {
    async function checkSubscription() {
      if (!isSupported) return;

      try {
        const registration = await navigator.serviceWorker.ready;
        const existingSubscription = await registration.pushManager.getSubscription();
        
        if (existingSubscription) {
          setSubscription(existingSubscription);
          setIsSubscribed(true);
        }
      } catch (error) {
        console.error('Error checking subscription:', error);
      }
    }

    checkSubscription();
  }, [isSupported]);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!isSupported) {
      throw new Error('Push notifications not supported');
    }

    try {
      // Request permission
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);

      if (permissionResult !== 'granted') {
        throw new Error('Notification permission denied');
      }

      // Register service worker
      const registration = await navigator.serviceWorker.register('/push-sw.js');
      await navigator.serviceWorker.ready;

      let newSubscription;
      
      if (vapidKey) {
        // True Web Push with VAPID
        newSubscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey)
        });
        console.log('Subscribed with VAPID key');
      } else {
        // Fallback: polling-based notifications
        console.warn('No VAPID key - using polling fallback');
        newSubscription = {
          endpoint: `polling://${userId || 'anonymous'}`,
          keys: { p256dh: '', auth: '' },
          toJSON: () => ({ endpoint: `polling://${userId || 'anonymous'}`, keys: {} })
        };
      }

      // Send subscription to server
      await api.post('/push/subscribe', {
        endpoint: newSubscription.endpoint,
        keys: newSubscription.toJSON?.()?.keys || newSubscription.keys || {},
        customer_id: userId,
        email: userId
      });

      setSubscription(newSubscription);
      setIsSubscribed(true);

      return { success: true, method: vapidKey ? 'webpush' : 'polling' };
    } catch (error) {
      console.error('Subscribe error:', error);
      return { success: false, error: error.message };
    }
  }, [isSupported, userId, vapidKey]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    if (!subscription) return { success: true };

    try {
      // Unsubscribe from browser
      if (subscription.unsubscribe) {
        await subscription.unsubscribe();
      }

      // Notify server
      await api.post('/push/unsubscribe', { endpoint: subscription.endpoint });

      setSubscription(null);
      setIsSubscribed(false);

      // Stop polling
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }

      return { success: true };
    } catch (error) {
      console.error('Unsubscribe error:', error);
      return { success: false, error: error.message };
    }
  }, [subscription]);

  // Poll for notifications (fallback when no VAPID or for pending notifications)
  const pollNotifications = useCallback(async () => {
    if (!userId) return [];

    try {
      const response = await api.get(`/push/pending/${encodeURIComponent(userId)}`);
      const data = response.data;

      // Show notifications using Notification API
      for (const notification of data.notifications || []) {
        if (Notification.permission === 'granted') {
          new Notification(notification.title, {
            body: notification.body,
            icon: notification.icon || '/logo192.png',
            tag: notification.tag,
            data: notification.data
          });
        }
      }

      return data.notifications || [];
    } catch (error) {
      console.error('Poll error:', error);
      return [];
    }
  }, [userId]);

  // Set up polling interval only for polling-based subscriptions
  useEffect(() => {
    // Only poll if subscribed via polling (no VAPID) or as supplementary check
    if (!isSubscribed || !userId) return;
    
    // Check if using polling endpoint
    const isPollingMode = subscription?.endpoint?.startsWith('polling://') || !vapidKey;
    
    if (isPollingMode) {
      // Poll more frequently for polling mode
      pollIntervalRef.current = setInterval(pollNotifications, 15000);
      pollNotifications(); // Initial poll
    } else {
      // Less frequent polling as backup for true push
      pollIntervalRef.current = setInterval(pollNotifications, 60000);
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [isSubscribed, userId, subscription, vapidKey, pollNotifications]);

  return {
    isSupported,
    isSubscribed,
    permission,
    loading,
    subscribe,
    unsubscribe,
    pollNotifications,
    vapidConfigured: !!vapidKey
  };
}

// Notification preferences hook
export function useNotificationPreferences(userId) {
  const [preferences, setPreferences] = useState({
    push_enabled: true,
    reward_alerts: true,
    streak_reminders: true,
    win_back_offers: true,
    new_items: true,
    quiet_hours_start: 22,
    quiet_hours_end: 8
  });
  const [loading, setLoading] = useState(true);

  // Fetch preferences
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    api.get(`/push/preferences/${encodeURIComponent(userId)}`)
      .then(res => {
        setPreferences(res.data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching preferences:', err);
        setLoading(false);
      });
  }, [userId]);

  // Update preferences
  const updatePreferences = useCallback(async (newPrefs) => {
    if (!userId) return { success: false };

    try {
      await api.put('/push/preferences', {
        customer_id: userId,
        ...preferences,
        ...newPrefs
      });
      setPreferences(prev => ({ ...prev, ...newPrefs }));
      return { success: true };
    } catch (error) {
      console.error('Error updating preferences:', error);
      return { success: false, error: error.message };
    }
  }, [userId, preferences]);

  return {
    preferences,
    loading,
    updatePreferences
  };
}

export default usePushNotifications;
